import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  signal,
  inject,
} from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  IFilter,
  IGrupo,
  IQueryParam,
  ITracker,
  IDashcam,
  IUsuario,
  IActivo,
  ICreateActivo,
  IUpdateActivo,
  ICliente,
  TipoVehiculo,
  FuncionActivo,
  IRecorrido,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { ActivosService } from '../../../../auxiliares/servicios/http/activos.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ParamsService } from '../../../../auxiliares/servicios/params.service';
import { CrearEditarTrackerComponent } from '../../administracion/trackers/crear-editar-tracker/crear-editar-tracker.component';
import {
  CrearEditarDashcamComponent,
  ICrearEditarDashcamData,
} from '../../administracion/dashcams/crear-editar-dashcam/crear-editar-dashcam.component';
import { CrearEditarAgrupacionesComponent } from '../../../standalone/grupos/crear-editar-grupos/crear-editar-grupos.component';
import { LoginService } from '../../../login/login.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { CrearEditarRecorridoVehiculosComponent } from '../../vehiculos/recorridos/crear-editar-recorrido/crear-editar-recorrido.component';
import { ChequeoIdentificacion } from '../../../../auxiliares/servicios/chequeo-identificacion';
import { esPatenteArgentinaValida } from '../../../../auxiliares/patente.util';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';

@Component({
  selector: 'app-crear-editar-vehiculo',
  templateUrl: './crear-editar-vehiculo.component.html',
  styleUrl: './crear-editar-vehiculo.component.scss',
  standalone: false,
})
export class CrearEditarVehiculoComponent implements OnInit, OnDestroy {
  private formularios = inject(FormularioDialogService);
  dialogRef = inject<MatDialogRef<CrearEditarVehiculoComponent>>(MatDialogRef, {
    optional: true,
  });
  helper = inject(HelperService);
  private service = inject(ActivosService);
  private lista2 = inject(Lista2Service);
  private paramsService = inject(ParamsService);
  data = inject<{
    vehiculo: IActivo;
  }>(MAT_DIALOG_DATA, { optional: true });

  public loading = false;
  public title?: string;
  public subtitle?: string;
  public clientePropio = LoginService.getCliente();

  //////////////////////////////////////////////////////////////
  public form?: FormGroup;
  @ViewChild('stepper') stepper?: MatStepper;
  public id?: string | null;

  /////////////////////////////////////////////////////////////////
  public vehiculo?: IActivo;

  public trackers?: ITracker[];
  public trackers$?: Subscription;

  public dashcams?: IDashcam[];
  public dashcams$?: Subscription;

  public recorridos = signal<IRecorrido[]>([]);
  public recorridos$?: Subscription;

  // Conductores para el ng-select (label = "Nombre (usuario)", orden alfabético).
  public conductores: (IUsuario & { label: string })[] = [];
  public conductores$?: Subscription;

  public tipoCliente = HelperService.getTipoCliente();

  public estados = ['Operativo', 'En mantenimiento', 'Fuera de servicio'];
  public tipos: TipoVehiculo[] = [
    'Auto',
    'Camion',
    'Camioneta',
    'Grua',
    'Moto',
    'Otro',
  ];
  public tiposLabel: Record<TipoVehiculo, string> = {
    Auto: 'Auto',
    Camion: 'Camión',
    Camioneta: 'Camioneta',
    Colectivo: 'Colectivo',
    Grua: 'Grúa',
    Moto: 'Moto',
    Otro: 'Otro',
  };
  public funciones: FuncionActivo[] = [
    'Particular',
    'Seguridad Privada',
    'Mantenimiento',
    'Transporte',
    'Bomberos',
    'Policia',
    'Ambulancia',
    'Servicio Técnico',
    'Otro',
  ];
  public funcionesLabel: Record<FuncionActivo, string> = {
    Particular: 'Particular',
    'Seguridad Privada': 'Seguridad Privada',
    Mantenimiento: 'Mantenimiento',
    Transporte: 'Transporte',
    Bomberos: 'Bomberos',
    Policia: 'Policía',
    Ambulancia: 'Ambulancia',
    'Servicio Técnico': 'Servicio Técnico',
    Otro: 'Otro',
  };
  // Opciones de los ng-select (valor guardado + etiqueta mostrada).
  public readonly opcionesTipo = this.tipos.map((valor) => ({
    valor,
    etiqueta: this.tiposLabel[valor],
  }));
  public readonly opcionesFuncion = this.funciones.map((valor) => ({
    valor,
    etiqueta: this.funcionesLabel[valor],
  }));
  public readonly opcionesModoConsumo = [
    { valor: 'km', etiqueta: 'Kilómetro (L/100km y km/L)' },
    { valor: 'hora', etiqueta: 'Hora de motor (L/h)' },
  ];

  ////Esto es algo que tiene que ver con el chofer
  get vehiculoForm() {
    return this.form?.get('vehiculo') as FormGroup;
  }

  /**
   * Patente cargada que no matchea ningún formato argentino. Es sólo una
   * advertencia (no invalida el formulario): se puede guardar cualquier valor,
   * pero con una patente así el vehículo no se sincroniza con Soflex.
   */
  get patenteInvalida(): boolean {
    const patente = this.vehiculoForm?.get('patente')?.value;
    return !!patente?.trim() && !esPatenteArgentinaValida(patente);
  }

  get idCliente() {
    return this.form?.get('idCliente')?.value;
  }

  public chequeo = new ChequeoIdentificacion<IActivo>({
    entidad: 'vehículo',
    articulo: 'un',
    campo: 'identificacion',
    control: () => this.form?.get('identificacion'),
    listar: (query) => this.service.getFiltered(query),
    idCliente: () => this.idCliente,
    excluirId: () => this.id,
    filtroExtra: () => ({ categoria: 'Vehículo' }),
  });

  ////////////////////////////////////////////////////////////////////////////
  //FORMULARIO

  public createForm() {
    // Determinar el cliente por defecto basado en el tipo de usuario
    const clienteDefault =
      this.tipoCliente === 'Final'
        ? this.clientePropio._id
        : (this.vehiculo?.idCliente ?? this.clientePropio?._id);

    this.form = new FormGroup({
      identificacion: new FormControl(
        this.vehiculo?.identificacion,
        Validators.required,
        this.chequeo.validador,
      ),
      idGrupo: new FormControl(this.vehiculo?.idGrupo),
      idTracker: new FormControl(this.vehiculo?.idTracker),
      idDashcam: new FormControl(this.vehiculo?.idDashcam),
      idCliente: new FormControl(clienteDefault, Validators.required),
      idsClientesQuePuedenAtender: new FormControl(
        this.vehiculo?.idsClientesQuePuedenAtender ?? null,
      ),
      idsClientesQuePuedenAtenderEventosTecnicos: new FormControl(
        this.vehiculo?.idsClientesQuePuedenAtenderEventosTecnicos ?? null,
      ),
      configHorariosAtencion: new FormControl(
        this.vehiculo?.configHorariosAtencion ??
          (this.clientePropio?._id
            ? [{ idCliente: this.clientePropio._id }]
            : null),
      ),
      configHorariosAtencionTecnica: new FormControl(
        this.vehiculo?.configHorariosAtencionTecnica ??
          (this.clientePropio?._id
            ? [{ idCliente: this.clientePropio._id }]
            : null),
      ),
      puedeSolicitarServicioTecnico: new FormControl(
        this.vehiculo?.puedeSolicitarServicioTecnico ?? false,
      ),
      categoria: new FormControl('Vehículo'),
      funcion: new FormControl(this.vehiculo?.funcion),
      vehiculo: new FormGroup({
        idExterno: new FormControl(this.vehiculo?.vehiculo?.idExterno),
        idChofer: new FormControl(this.vehiculo?.vehiculo?.idChofer),
        patente: new FormControl(this.vehiculo?.vehiculo?.patente),
        estado: new FormControl(this.vehiculo?.vehiculo?.estado || 'Operativo'),
        tipo: new FormControl(this.vehiculo?.vehiculo?.tipo),
        marca: new FormControl(this.vehiculo?.vehiculo?.marca),
        modelo: new FormControl(this.vehiculo?.vehiculo?.modelo),
        anio: new FormControl(this.vehiculo?.vehiculo?.anio),
        consumoRuta: new FormControl(this.vehiculo?.vehiculo?.consumoRuta, [
          Validators.min(0),
        ]),
        consumoCiudad: new FormControl(this.vehiculo?.vehiculo?.consumoCiudad, [
          Validators.min(0),
        ]),
        capacidadCombustible: new FormControl(
          this.vehiculo?.vehiculo?.capacidadCombustible,
          [Validators.min(0)],
        ),
        modoConsumo: new FormControl(
          this.vehiculo?.vehiculo?.modoConsumo ?? 'km',
        ),
        idRecorrido: new FormControl(this.vehiculo?.vehiculo?.idRecorrido),
      }),
    });
  }

  //////////////////////////////////////////////////////////////////////////////
  ///LISTADOS
  public async listarRecorridos() {
    if (this.form?.value?.idCliente) {
      const filter: IFilter<IRecorrido> = {
        categoria: 'Vehiculo',
        idCliente: this.form.value.idCliente,
      };
      const query: IQueryParam = {
        sort: 'nombre',
        select: 'nombre',
        filter: JSON.stringify(filter),
        limit: 0,
        includeChildren: true,
      };
      this.recorridos$?.unsubscribe();
      this.recorridos$ = this.lista2.recorridos(query, 30).subscribe((data) => {
        this.recorridos.set(data.datos);
      });
    }
  }

  // Etiqueta visible/buscable del conductor: "Nombre completo (usuario)".
  private armarLabelConductor(u: IUsuario): string {
    const nombre = u.datosPersonales?.nombre?.trim();
    return nombre ? `${nombre} (${u.usuario})` : (u.usuario ?? '');
  }

  public listarConductores(): void {
    // Solo conductores del propio cliente del vehículo (sin includeChildren).
    const idCliente = this.form?.value?.idCliente;
    this.conductores$?.unsubscribe();
    if (!idCliente) {
      this.conductores = [];
      return;
    }
    // includeChildren:true para que el scope alcance al cliente del vehículo si
    // es un cliente hijo; el filtro por idCliente lo acota a ese cliente exacto.
    const query: IQueryParam = {
      filter: JSON.stringify({ idCliente }),
      includeChildren: true,
      childrenLevel: 0,
      sort: 'usuario',
    };
    this.conductores$ = this.lista2.conductors(query).subscribe((res) => {
      this.conductores = (res.datos || [])
        .filter((u) => String(u.idCliente) === String(idCliente))
        .map((u) => ({ ...u, label: this.armarLabelConductor(u) }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }),
        );
    });
  }

  public async listarTrackers() {
    if (this.form?.value?.idCliente) {
      const filter: IFilter<ITracker> = {
        $and: [
          { idCliente: this.form?.value?.idCliente },
          {
            $or: [
              {
                asignadoA: null,
              },
              {
                asignadoA: { $exists: false },
              },
            ],
          },
        ],
      };
      if (this.vehiculo?._id) {
        filter.$and[1].$or.push({
          _id: this.vehiculo?.idTracker,
        });
      }
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        sort: 'identificacion',
        limit: 0,
        includeChildren: true,
      };
      //
      this.trackers$?.unsubscribe();
      this.trackers$ = this.lista2.trackers(query, 30).subscribe((data) => {
        const trackers = JSON.parse(JSON.stringify(data.datos));
        this.trackers = trackers;
      });
    }
  }

  /** Dashcams del cliente sin asignar, más la del vehículo en edición. */
  public listarDashcams(): void {
    const idCliente = this.form?.value?.idCliente;
    this.dashcams$?.unsubscribe();
    if (!idCliente) {
      this.dashcams = [];
      return;
    }
    const libres: IFilter<IDashcam>[] = [
      { asignadoA: null },
      { asignadoA: { $exists: false } },
    ];
    if (this.vehiculo?.idDashcam) libres.push({ _id: this.vehiculo.idDashcam });
    const filter: IFilter<IDashcam> = {
      $and: [{ idCliente }, { $or: libres }],
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      select: 'nombre identificacion uniqueId',
      sort: 'nombre',
      limit: 0,
      includeChildren: true,
    };
    this.dashcams$ = this.lista2.dashcams(query, 30).subscribe((data) => {
      this.dashcams = data.datos;
    });
  }

  ////////////////////////////////////////////////////////////////////////
  public async onSubmit() {
    this.loading = true;
    try {
      if (!(await this.chequeo.puedeGuardar())) {
        this.helper.notifError(`${this.chequeo.textoRepetida} Elegí otra.`);
        this.loading = false;
        return;
      }

      const data = this.getData();

      if (this.id) {
        // Editar
        if (data) {
          await this.service.update(this.id, data);
          this.helper.notifSuccess('Vehículo actualizado correctamente');
          this.helper.volver();
        }
      } else {
        // Crear
        const data = this.getData();
        if (data) {
          await this.service.create(data);
          this.helper.notifSuccess('Vehículo creado correctamente');
          this.helper.volver();
        }
      }
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  public async onClienteChange(cliente: ICliente) {
    // ---- reset de selects relacionados ----
    this.form.patchValue({
      idTracker: null,
      idDashcam: null,
      idGrupo: null,
      idRecorrido: null,
      idsClientesQuePuedenAtenderEventosTecnicos: null,
      puedeSolicitarServicioTecnico: false,
    });
    this.vehiculoForm.patchValue({ idChofer: null });

    // El mensaje de identificación repetida aclara el cliente: revalidar.
    this.chequeo.revalidar();

    // Filtrar horarios: conservar solo clientePropio si estaba seleccionado
    const propioId = this.clientePropio?._id;
    for (const ctrl of [
      'configHorariosAtencion',
      'configHorariosAtencionTecnica',
    ]) {
      const current = this.form.get(ctrl)?.value;
      if (current?.length) {
        const filtered = current.filter((c: any) => c.idCliente === propioId);
        this.form.get(ctrl)?.patchValue(filtered.length ? filtered : null);
      }
    }

    // ---- carga de datos dependientes ----
    if (cliente) {
      this.listarConductores();
      this.listarDashcams();
      await Promise.all([this.listarTrackers(), this.listarRecorridos()]);
    } else {
      this.trackers = [];
      this.dashcams = [];
      this.recorridos.set([]);
      this.conductores = [];
    }
  }

  private getData() {
    const data: ICreateActivo | IUpdateActivo = this.form?.value;
    data.categoria = 'Vehículo';
    const idsAtencion = (data.configHorariosAtencion ?? [])
      .map((c: any) => c.idCliente)
      .filter(Boolean) as string[];
    const idsTecnicos = (data.configHorariosAtencionTecnica ?? [])
      .map((c: any) => c.idCliente)
      .filter(Boolean) as string[];
    data.idsClientesQuePuedenAtender = idsAtencion;
    data.idsClientesQuePuedenAtenderEventosTecnicos = idsTecnicos.length
      ? idsTecnicos
      : null;
    return data;
  }

  /////////////////////////////////////////////////////////////////////////////
  //CREAR

  public crearTracker() {
    this.paramsService.setParams({
      desdeActivo: true,
      idCliente: this.form.value.idCliente,
    });
    const dialog = this.formularios.abrir(CrearEditarTrackerComponent, {
      tamano: 'grande',
    });
    dialog.afterClosed().subscribe((res) => {
      if (res) {
        this.form.patchValue({ idTracker: res });
      }
      this.paramsService.setParams({ vehiculo: this.vehiculo });
    });
  }
  /** Alta de una dashcam en el cliente del vehículo; vuelve ya elegida. */
  public crearDashcam(): void {
    const idCliente = this.form?.value?.idCliente;
    if (!idCliente) return;
    this.formularios
      .abrir<CrearEditarDashcamComponent, ICrearEditarDashcamData, IDashcam>(
        CrearEditarDashcamComponent,
        { tamano: 'grande', data: { idCliente } },
      )
      .afterClosed()
      .subscribe((res) => {
        if (!res?._id) return;
        this.listarDashcams();
        this.form.patchValue({ idDashcam: res._id });
      });
  }
  public crearGrupo() {
    const dialog = this.formularios.abrir(CrearEditarAgrupacionesComponent, {
      tamano: 'mediano',
      data: { categoria: 'Flota' },
    });
    dialog.afterClosed().subscribe((res) => {
      if (res) {
        this.form.patchValue({ idGrupo: res });
      }
    });
  }
  public crearRecorrido() {
    const dialog = this.formularios.abrir(
      CrearEditarRecorridoVehiculosComponent,
      { tamano: 'grande' },
    );
    dialog.afterClosed().subscribe((res) => {
      if (res) {
        this.vehiculoForm.patchValue({ idRecorrido: res });
        this.listarRecorridos();
      }
    });
  }

  ///////////////////////////////////////////////////////////
  public volver() {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      HelperService.volver();
    }
  }

  private setTitles() {
    if (this.vehiculo) {
      this.title = 'Editar Vehículo';
      this.subtitle = this.vehiculo?.identificacion;
    } else {
      this.title = 'Crear Vehículo';
    }
  }

  /// HOOKS

  async ngOnInit() {
    this.loading = true;
    this.vehiculo =
      this.paramsService.getParams()['vehiculo'] || this.data?.vehiculo;
    this.id = this.vehiculo?._id || this.data?.vehiculo?._id;
    this.setTitles();
    this.createForm();

    // Preseleccionar el cliente del permiso activo al crear
    if (!this.id) {
      this.form.patchValue({
        idCliente: this.clientePropio?._id,
      });
    }

    this.listarConductores();
    this.listarDashcams();
    await Promise.all([this.listarTrackers(), this.listarRecorridos()]);

    this.loading = false;
  }

  ngOnDestroy(): void {
    this.trackers$?.unsubscribe();
    this.dashcams$?.unsubscribe();
    this.recorridos$?.unsubscribe();
    this.conductores$?.unsubscribe();
  }
}
